/**
 * Reducing the colour count of artwork that was built by stacking.
 *
 * A generated illustration is painted in depth order: a base colour covers the
 * frame, larger shapes go down over it, and details land last. The reference
 * file shows it plainly — its base colour is painted first and spans 130% of the
 * canvas in overlapping shapes, while a colour introduced at path 232 covers 5%
 * and sits entirely on top of others. That order is information, and it is the
 * only information about depth an SVG carries.
 *
 * So this does NOT reduce colours by perceptual similarity, which is what the
 * raster tracer does and what a naive port would do here. Merging two similar
 * colours that sit at different depths repaints a highlight in the shade of the
 * thing beside it and the form collapses. Merging a colour into the colour it
 * SITS ON is instead exactly what deleting that layer would reveal, so the
 * subject keeps its shape until very few colours remain — which is the property
 * that makes cutting a 300-layer trace to 100 look almost identical.
 *
 * Depth is recovered from paint order plus overlap: for each shape, the last
 * shape painted BEFORE it that covers its centre is what it sits on. Aggregated
 * per colour, the most common substrate is that colour's floor.
 */

import { changed, rejected, type Command, type CommandExecution } from '../core/commands';
import { damage } from '../core/damage';
import { findLayer, type EditorDocument, type LayerId, type VectorLayer } from '../core/model';
import { objectBounds } from './select-ops';
import type { VectorObject } from './types';

export interface ColourStackEntry {
  /** Canonical fill, lowercase hex. */
  readonly colour: string;
  readonly objects: number;
  /** Summed bounding-box area, the cheap stand-in for how much frame it holds. */
  readonly weight: number;
  /** Index of the first object painted in this colour; low means structural. */
  readonly firstPainted: number;
  /** The colour it most often sits on, or null for the floor of the stack. */
  readonly sitsOn: string | null;
}

export interface ColourStack {
  /** Bottom of the stack first, by first appearance. */
  readonly entries: readonly ColourStackEntry[];
  /**
   * Ordered removals. Applying the first k means k fewer colours, each repainted
   * as whatever it was sitting on.
   */
  readonly merges: readonly { readonly from: string; readonly into: string }[];
}

const fillOf = (object: VectorObject): string | null => (object.fill && object.fill.alpha > 0 ? object.fill.color.toLowerCase() : null);

export function analyseColourStack(objects: readonly VectorObject[]): ColourStack {
  interface Placed { colour: string; minX: number; minY: number; maxX: number; maxY: number; cx: number; cy: number; area: number }
  const placed: Placed[] = [];
  for (const object of objects) {
    const colour = fillOf(object);
    const bounds = colour ? objectBounds(object) : null;
    if (!colour || !bounds) continue;
    placed.push({
      colour,
      minX: bounds.x, minY: bounds.y, maxX: bounds.x + bounds.width, maxY: bounds.y + bounds.height,
      cx: bounds.x + bounds.width / 2, cy: bounds.y + bounds.height / 2,
      area: Math.max(0, bounds.width * bounds.height),
    });
  }

  const stats = new Map<string, { objects: number; weight: number; firstPainted: number; substrates: Map<string, number> }>();
  placed.forEach((shape, index) => {
    const entry = stats.get(shape.colour) ?? { objects: 0, weight: 0, firstPainted: index, substrates: new Map<string, number>() };
    entry.objects += 1;
    entry.weight += shape.area;
    entry.firstPainted = Math.min(entry.firstPainted, index);
    // Walk back to the most recent shape painted underneath this one's centre.
    for (let below = index - 1; below >= 0; below -= 1) {
      const under = placed[below]!;
      if (shape.cx < under.minX || shape.cx > under.maxX || shape.cy < under.minY || shape.cy > under.maxY) continue;
      if (under.colour !== shape.colour) entry.substrates.set(under.colour, (entry.substrates.get(under.colour) ?? 0) + 1);
      break;
    }
    stats.set(shape.colour, entry);
  });

  const entries: ColourStackEntry[] = [...stats.entries()]
    .map(([colour, entry]) => {
      let sitsOn: string | null = null;
      let best = 0;
      for (const [substrate, count] of entry.substrates) {
        if (count > best || (count === best && sitsOn !== null && substrate < sitsOn)) { best = count; sitsOn = substrate; }
      }
      return { colour, objects: entry.objects, weight: entry.weight, firstPainted: entry.firstPainted, sitsOn };
    })
    .sort((left, right) => left.firstPainted - right.firstPainted);

  // The floor is whatever was painted FIRST, not whatever the substrate vote
  // says. The base colour's later shapes do sit on things — the reference file's
  // base wins a vote for sitting on white — but the colour laid down before
  // anything else is the one with nothing to reveal beneath it.
  const floor = entries[0]?.colour ?? null;

  // Removal order: the colour that holds least of the frame goes first, and a
  // tie goes to whichever was painted later — a late, small colour is a detail,
  // and an early, large one is structure.
  const removable = [...entries]
    .filter((entry) => entry.colour !== floor)
    .sort((left, right) => left.weight - right.weight || right.firstPainted - left.firstPainted);
  const merges: { from: string; into: string }[] = [];
  const resolved = new Map<string, string>();
  const resolve = (colour: string): string => {
    let current = colour;
    for (let step = 0; step < entries.length && resolved.has(current); step += 1) current = resolved.get(current)!;
    return current;
  };
  for (const entry of removable) {
    // A colour with nothing under it anywhere — a shape floating clear of the
    // artwork — still has to go somewhere, or the count control would stop
    // short of the level the user asked for. The floor is the honest answer.
    const substrate = entry.sitsOn && entry.sitsOn !== entry.colour ? entry.sitsOn : floor;
    if (!substrate) continue;
    let into = resolve(substrate);
    // Two colours can sit on each other — a highlight on a shadow on the same
    // highlight — and once the first is merged the second resolves back to
    // itself. Left unhandled that colour can never be removed and the control
    // stops one short of the level asked for.
    if (into === entry.colour) into = floor && floor !== entry.colour ? resolve(floor) : entry.colour;
    if (into === entry.colour) continue;
    resolved.set(entry.colour, into);
    merges.push({ from: entry.colour, into });
  }
  return { entries, merges };
}

/**
 * Colour -> the colour that stands in for it at `count` colours.
 *
 * Identity when nothing needs merging, so a caller can apply it unconditionally.
 */
export function colourStackRemap(stack: ColourStack, count: number): Map<string, string> {
  const total = stack.entries.length;
  const target = Math.max(1, Math.min(total, Math.round(count)));
  const steps = Math.max(0, total - target);
  const map = new Map<string, string>();
  for (const entry of stack.entries) map.set(entry.colour, entry.colour);
  for (let step = 0; step < steps && step < stack.merges.length; step += 1) {
    const merge = stack.merges[step]!;
    const into = map.get(merge.into) ?? merge.into;
    // Anything already pointing at the colour being removed follows it down.
    for (const [colour, current] of map) if (current === merge.from) map.set(colour, into);
    map.set(merge.from, into);
  }
  return map;
}

/** How many distinct colours survive at `count`. */
export function colourStackLevels(stack: ColourStack): number[] {
  return Array.from({ length: stack.entries.length }, (_, index) => index + 1);
}

/* ---------- applying it ---------- */

export const COLOUR_STACK_KEY = 'maipo:colour-stack';

interface StackOrigin { readonly version: 1; readonly fills: Record<string, { color: string; swatchId?: string }> }

/**
 * The artwork's colours as they were before any reduction.
 *
 * Captured once and kept, because the analysis has to run on the ORIGINAL stack.
 * Re-deriving it from already-merged artwork would compute depth for a picture
 * that no longer exists, and dragging the count back up could never restore what
 * was there.
 */
export function readStackOrigin(layer: VectorLayer): StackOrigin | null {
  const raw = layer.vector.extensions[COLOUR_STACK_KEY];
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Partial<StackOrigin>;
  return value.version === 1 && value.fills && typeof value.fills === 'object' ? { version: 1, fills: value.fills } : null;
}

/** Objects restored to their original fills, for analysing or resetting. */
function originalObjects(layer: VectorLayer, origin: StackOrigin | null): VectorObject[] {
  if (!origin) return [...layer.vector.objects];
  return layer.vector.objects.map((object) => {
    const fill = origin.fills[object.id];
    return fill && object.fill ? { ...object, fill: { ...object.fill, color: fill.color, ...(fill.swatchId ? { swatchId: fill.swatchId } : {}) } } : object;
  });
}

/** The stack of a layer, always measured on its original colours. */
export function layerColourStack(layer: VectorLayer): ColourStack {
  return analyseColourStack(originalObjects(layer, readStackOrigin(layer)));
}

/**
 * Repaints a layer at `colours` distinct colours.
 *
 * Always applied to the ORIGINAL fills rather than the current ones, so the
 * control is a dial the user can move both ways rather than a ratchet.
 */
export class ReduceColoursCommand implements Command {
  readonly label = 'Reduce colours';
  private before?: { fills: Map<string, { color: string; swatchId?: string }>; hadOrigin: boolean; origin: unknown };

  constructor(private readonly layerId: LayerId, private readonly colours: number) {}

  retainedBytes(): number { return 256 + (this.before?.fills.size ?? 0) * 96; }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'vector') return rejected('Select vector artwork to reduce its colours.');
    if (layer.locked) return rejected('Layer is locked.');

    this.before ??= {
      fills: new Map(layer.vector.objects.flatMap((object) => (object.fill
        ? [[object.id, { color: object.fill.color, ...(object.fill.swatchId ? { swatchId: object.fill.swatchId } : {}) }] as const]
        : []))),
      hadOrigin: Object.hasOwn(layer.vector.extensions, COLOUR_STACK_KEY),
      origin: structuredClone(layer.vector.extensions[COLOUR_STACK_KEY] ?? null),
    };

    let origin = readStackOrigin(layer);
    if (!origin) {
      // First reduction on this layer: remember what it looked like.
      const fills: StackOrigin['fills'] = {};
      for (const object of layer.vector.objects) {
        if (object.fill) fills[object.id] = { color: object.fill.color, ...(object.fill.swatchId ? { swatchId: object.fill.swatchId } : {}) };
      }
      origin = { version: 1, fills };
      layer.vector.extensions[COLOUR_STACK_KEY] = origin;
    }

    const stack = analyseColourStack(originalObjects(layer, origin));
    if (stack.entries.length === 0) return rejected('This layer has no filled artwork.');
    const remap = colourStackRemap(stack, this.colours);
    // A surviving colour keeps its swatch, so recolouring still works afterwards.
    const swatchOfColour = new Map<string, string | undefined>();
    for (const [id, fill] of Object.entries(origin.fills)) {
      void id;
      if (!swatchOfColour.has(fill.color.toLowerCase())) swatchOfColour.set(fill.color.toLowerCase(), fill.swatchId);
    }
    for (const object of layer.vector.objects) {
      const source = origin.fills[object.id];
      if (!object.fill || !source) continue;
      const target = remap.get(source.color.toLowerCase()) ?? source.color;
      object.fill.color = target;
      const swatchId = swatchOfColour.get(target);
      if (swatchId) object.fill.swatchId = swatchId;
      else delete object.fill.swatchId;
    }
    return changed(damage({ layerIds: [this.layerId] }));
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'vector' || !this.before) return;
    for (const object of layer.vector.objects) {
      const fill = this.before.fills.get(object.id);
      if (!object.fill || !fill) continue;
      object.fill.color = fill.color;
      if (fill.swatchId) object.fill.swatchId = fill.swatchId;
      else delete object.fill.swatchId;
    }
    if (this.before.hadOrigin) layer.vector.extensions[COLOUR_STACK_KEY] = structuredClone(this.before.origin);
    else delete layer.vector.extensions[COLOUR_STACK_KEY];
  }
}
